import React, { useEffect, useState } from "react";
import Alert from '@qc/particle/lib/components/alert'
import Button from '@qc/particle/lib/components/button'
import Form from '@qc/particle/lib/components/form'
import Input from '@qc/particle/lib/components/input'
import Space from '@qc/particle/lib/components/space'
import Tooltip from '@qc/particle/lib/components/tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQuestionCircle } from '@fortawesome/pro-light-svg-icons/faQuestionCircle'

import { connect } from "react-redux"
import { getForwardUrl } from "../oktaConfigUtil"
import { getValidRedirect } from '../utils/whiteListRedirect'

import * as Stld from "./ActivateView.styles"
import * as UserActivationActions from '../store/reducers/user_activation/actions'
import PasswordInput, { PASSWORD_VALIDATOR } from "../components/PasswordInput"
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk'

const ActivateView = ({
  activation: {
    error,
    errorSummary,
    loading
  },
  activateUser,
  location
}) => {
  const urlParams = new URLSearchParams(location.search);
  const loginHint = urlParams.get('login_hint');
  const firstNameHint = urlParams.get('first_name');
  const lastNameHint = urlParams.get('last_name');
  const [form] = Form.useForm();
  const activationToken = window.location.pathname.split("/")[2];
  const forwardUrl = getForwardUrl();
  const redirectUrl = getValidRedirect(forwardUrl);
  const [signed, setSigned] = useState((firstNameHint && lastNameHint) ? true : false)

  
  useEffect(() => {
    if (firstNameHint) {
      form.setFieldsValue({ firstName: firstNameHint });
    }
    if (lastNameHint) {
      form.setFieldsValue({ lastName: lastNameHint });
    }
  }, [firstNameHint, lastNameHint])
  

  const _activateUser = (formData) => {
    activateUser(
      loginHint,
      formData.firstName,
      formData.lastName,
      formData.password,
      activationToken,
      redirectUrl
    );
  };

  const labelWithTooltip = (
    <Space>
      Email Address
      <Tooltip
        id="tooltip"
        title="Your email address is set in Quantcast. Please contact you IT administrator to change it"
        placement="top"
      >
        <FontAwesomeIcon icon={faQuestionCircle} />
      </Tooltip>
    </Space>
  );

  return (
    <Stld.Content>
      <Stld.Card>
        <div>
          <div data-cy="header">
            <Stld.H3 data-cy="qText">Activate your Account</Stld.H3>
          </div>
          {error === "403" ? <Alert message={errorSummary} type="error" showIcon data-cy="errorMessageTokenExpired" /> :
            <Form
              form={form}
              layout="vertical"
              onFinish={(formData) => _activateUser(formData)}
              requiredMark={false}
            >
              {!!loginHint ? 
                <Stld.FormControl
                  data-cy="emailAddressControl"
                  label={labelWithTooltip}
                  value={loginHint}
                >
                  <Stld.FilledValue>{loginHint}</Stld.FilledValue>
                </Stld.FormControl> : null }

              <Stld.FormControl
                data-cy="firstNameControl"
                label="First Name"
              >
                <Stld.FormControl
                  noStyle
                  name="firstName"
                  rules={[
                    {required: true, message: 'Please input your first name!' },
                    {whitespace: true, message: 'First name cannot be a whitespace'}
                  ]}
                >
                  <Input data-cy="firstNameInput" placeholder="Type in your name" type={firstNameHint ? 'hidden' : 'text'} size="large"  />
                </Stld.FormControl>
                {firstNameHint ? <Stld.FilledValue>{firstNameHint}</Stld.FilledValue> : null}
              </Stld.FormControl>
              <Stld.FormControl
                data-cy="lastNameControl"
                label="Last Name"
              >
                <Stld.FormControl
                  noStyle
                  name="lastName"
                  rules={[
                    {required: true, message: 'Please input your last name!' },
                    {whitespace: true, message: 'First name cannot be a whitespace'}
                  ]}
                >
                  <Input data-cy="lastNameInput" placeholder="Type in your last name" type={lastNameHint ? 'hidden' : 'text'} size="large" />
                </Stld.FormControl>
                {lastNameHint ? <Stld.FilledValue>{lastNameHint}</Stld.FilledValue> : null }
              </Stld.FormControl>

              <Stld.FormControl
                data-cy="passwordControl"
                label="Password"
                name="password"
                rules={[
                  {required: true, message: ''},
                  PASSWORD_VALIDATOR(loginHint),
                ]}
              >
                <PasswordInput
                  username={loginHint}
                  data-cy="passwordInput"
                  placeholder="Type your new password here"
                  id="newPassword"
                  size="large" 
                />
              </Stld.FormControl>

              <Stld.FormControl
                data-cy="confirmPasswordControl"
                label="Confirm Password"
                name="confirmPassword"
                rules={[
                  {required: true, message: 'Please confirm your password!' },
                  (form) => ({
                    validator(_, value) {
                      if (!value || form.getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject();
                    },
                    message: 'The two passwords that you entered do not match!'
                  }),
                ]}
              >
                <Input.Password
                  data-cy="confirmPasswordInput"
                  placeholder="Retype your new password here"
                  id="confirmPassword"
                  size="large" 
                />
              </Stld.FormControl>
              {!lastNameHint || !firstNameHint ? 
                <PSClickWrap
                  clickWrapStyle="combined"
                  accessId={process.env.REACT_APP_PACT_SAFE_ACCESS_ID || ''}
                  groupKey={process.env.REACT_APP_PACT_SAGE_GROUP_KEY || ''}
                  signerId={loginHint}
                  onValid={() => setSigned(true)}
                  onInvalid={() => setSigned(false)}
                /> : null
              }
              <Stld.FormControl>
                  <Button type="primary" htmlType="submit" loading={loading} disabled={loading || !signed}>
                      Save
                  </Button>
              </Stld.FormControl>
              {(error === "500" || error === "400") ? <Stld.ErrorMessageApi data-cy="errorMessageApi"><p>{errorSummary}</p></Stld.ErrorMessageApi> : null}
            </Form>
          }
        </div>
      </Stld.Card>
    </Stld.Content>
  );
};

const mapStateToProps = state => ({
  profile: state.profile,
  activation: state.activation,
  loading: state.loading
});

const mapDispatchToProps = dispatch => ({
  activateUser: (loginHint, firstName, lastName, password, activationToken, redirectUrl) =>
    dispatch(UserActivationActions.activateUser({ loginHint, firstName, lastName, password, activationToken, redirectUrl }))
});

export default connect(mapStateToProps, mapDispatchToProps)(ActivateView);
